// AddContactDialog — Session 7 PR3.
//
// Inline dialog opened from the Outreach page header. Mirrors the Resumes
// upload form's tone — narrow, vertical, minimal. company_id picker is a
// plain Select sourced from /api/companies + an "Other" sentinel for ad-hoc.

import { useState, type FormEvent } from "react";
import type { ReactNode } from "react";
import { Dialog } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import { Select } from "../ui/Select";
import { useCompanies } from "../../lib/companies";
import { useCreateContact } from "../../lib/contacts";

const OTHER_VALUE = "__other__";

export function AddContactDialog({ trigger }: { trigger: ReactNode }) {
  const companies = useCompanies();
  const create = useCreateContact();
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [companyId, setCompanyId] = useState<string>(OTHER_VALUE);
  const [role, setRole] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  function reset() {
    setFullName("");
    setCompanyId(OTHER_VALUE);
    setRole("");
    setLinkedinUrl("");
    setEmail("");
    setNotes("");
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!fullName.trim()) return;
    create.mutate(
      {
        full_name: fullName.trim(),
        company_id: companyId === OTHER_VALUE ? null : companyId,
        role: role.trim() || null,
        linkedin_url: linkedinUrl.trim() || null,
        email: email.trim() || null,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          reset();
          setOpen(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Content
        title="Add contact"
        description="Person you've talked to or want to reach. Survives across applications."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!fullName.trim() || create.isPending}
            >
              {create.isPending ? "Saving…" : "Add contact"}
            </Button>
          </>
        }
      >
        <form className="space-y-3" onSubmit={submit}>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">
              Full name
            </label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoFocus
              required
              placeholder="Jordan Recruiter"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">
              Company
            </label>
            <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value={OTHER_VALUE}>Other / unknown</option>
              {(companies.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-neutral-500">
                Role at their company
              </label>
              <Input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="VP Engineering"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-neutral-500">
                Email
              </label>
              <Input
                value={email}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jordan@acme.com"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">
              LinkedIn URL
            </label>
            <Input
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://linkedin.com/in/jordan"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Where you met, mutuals, any context…"
              rows={3}
            />
          </div>
        </form>
      </Dialog.Content>
    </Dialog>
  );
}
